import React, {useState, useEffect} from 'react';
import useAsyncEffect from 'use-async-effect';
import {connect} from 'react-redux';
import {match} from 'react-router-dom';
import moment, {Moment} from 'moment';
import {ThunkDispatch} from 'redux-thunk';
import {AnyAction} from 'redux';
import {toast} from 'react-toastify';

import {AppState} from '../Store';
import {Survey, SurveyService} from '../Services/SurveyService';
import {Page} from '../Models';
import {loadSurveysCreator} from '../Store/Admin/Actions';

import DateTime from 'react-datetime';
import {Link} from 'react-router-dom';
import SurveyPreview from './SurveyPreview';
import LoadMore from '../Components/LoadMore';

type TParams = {
  id?: string
};

interface SurveysProps {
  match: match<TParams>;

  surveyService: SurveyService;
  surveys: Page<Survey>;

  setPage: (page: number) => Promise<void>;
}

function Surveys({match, surveyService, surveys, setPage}: SurveysProps) {
  const [survey, setSurvey] = useState<Survey | undefined>(undefined);
  const [saving, setSaving] = useState(false);

  useAsyncEffect(async isMounted => {
    const {params: {id}} = match;
    if (survey === undefined && id !== undefined) {
      if (id === 'new') {
        setSurvey({
          id: -1,
          title: '',
          embedCode: '',
          date: new Date(),
          expiry: new Date(),
        });
      }
      try {
        const res = await surveyService.byId(Number(id));
        if (res.parsedBody !== undefined && isMounted()) {
          setSurvey(res.parsedBody);
        }
      } catch (e) {
        console.log(e);
      }
    }
  },[match]);

  useEffect(() => {
    if (surveys.page === 1 && surveys.items.length === 0) {
      setPage(1);
    }
  }, [surveys, setPage]);

  const titleChanged = (e: React.FormEvent<HTMLInputElement>) => {
    if (survey === undefined) {
      return;
    }
    setSurvey({
      ...survey,
      title: e.currentTarget.value,
    });
  };

  const embedCodeChanged = (e: React.FormEvent<HTMLInputElement>) => {
    if (survey === undefined) {
      return;
    }
    setSurvey({
      ...survey,
      embedCode: e.currentTarget.value,
    });
  };

  const dateChanged = (date: Moment | string) => {
    if (survey === undefined) {
      return;
    }

    setSurvey({
      ...survey,
      date: moment(date).toDate(),
    });
  };

  const expiryChanged = (date: Moment | string) => {
    if (survey === undefined) {
      return;
    }

    setSurvey({
      ...survey,
      expiry: moment(date).toDate(),
    });
  };

  const save = async () => {
    if (survey === undefined) {
      toast.info("Honestly, I'm kinda impressed you managed to do that");
      return;
    }

    setSaving(true);

    if (survey.id === -1) {
      try {
        const res = await surveyService.create(survey);
        setSurvey(res.parsedBody);
        toast.success("Survey created!");
      } catch {
        toast.error("Failed to save survey");
      }
    } else {
      try {
        const res = await surveyService.update(survey);
        setSurvey(res.parsedBody);
        toast.success("Survey saved!");
      } catch {
        toast.error("Failed to save survey");
      }
    }

    setSaving(false);
  };

  const back = () => {
    setSurvey(undefined);
  };

  const {page, pageCount} = surveys;

  if (survey === undefined) {
    return (
      <div>
        <div className="post-content-area mb-100">
          <div className="world-category-area">
            <ul className="nav nav-tabs" role="tablist">
              <li className="title"><Link to="/admin/surveys/new">New Survey</Link></li>
            </ul>
            <div className="page-container">
              <div className="pages">
                <LoadMore digits={2} currentPage={page} pages={pageCount} callback={setPage} fragment="/admin/surveys/" />
              </div>
            </div>
            <div className="tab-content">
              {surveys.items.map(s => {
                return <SurveyPreview key={s.id} survey={s} />;
              })}
            </div>
          </div>
        </div>
        <div className="page-container">
          <div className="pages">
            <LoadMore digits={2} currentPage={page} pages={pageCount} callback={setPage} fragment="/admin/surveys/" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="post-content-area mb-100">
        <div className="world-category-area">
          <ul className="nav nav-tabs" role="tablist">
            <li className="title"><Link to="/admin/surveys" onClick={back}>Back</Link></li>
          </ul>
        </div>
      </div>
      <div className="article-edit">
        <fieldset disabled={saving}>
          <div className="row">
            <label className="col-sm-2">ID</label>
            <div className="col-sm-10">
              {survey.id}
            </div>
          </div>
          <div className="row">
            <div className="col-sm-6">
              <div className="form-group row">
                <label className="col-sm-2">Title</label>
                <div className="col-sm-10">
                  <input className="form-control" type="text" onChange={titleChanged} value={survey.title} />
                </div>
              </div>
              <div className="form-group row">
                <label className="col-sm-2">Embed Code</label>
                <div className="col-sm-10">
                  <input className="form-control" type="text" onChange={embedCodeChanged} value={survey.embedCode} />
                </div>
              </div>
              <div className="form-group row">
                <label className="col-sm-2">Start Date</label>
                <div className="col-sm-10">
                  <DateTime value={survey.date} onChange={dateChanged} />
                </div>
              </div>
              <div className="form-group row">
                <label className="col-sm-2">Expiry</label>
                <div className="col-sm-10">
                  <DateTime value={survey.expiry} onChange={expiryChanged} />
                </div>
              </div>
            </div>
          </div>
          <button className="btn btn-primary" type="submit" onClick={save}>Save</button>
        </fieldset>
      </div>
    </div>
  );
}

const mapStateToProps = (state: AppState) => {
  return {
    surveyService: state.auth.surveyService,
    surveys: state.admin.surveys.list,
  };
};

const mapDispatchToProps = (dispatch: ThunkDispatch<any, any, AnyAction>) => {
  return {
    setPage: (page: number) => dispatch(loadSurveysCreator(page)),
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(Surveys);
